import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { initializePaddle } from '@paddle/paddle-js';
import { useAuth } from '../context/FirebaseAuthContext';
import { PADDLE_CHECKOUT_SETTINGS } from '../constants/paddle';
import { getGuestSession, createGuestSession } from '../utils/guestSession';
import { supabase } from '../lib/supabase/client';
import { hasActiveSubscriptionByEmail } from '../utils/supabase-subscription-checker';

// Replace these with your actual price IDs from Paddle
const MONTHLY_PRICE_ID = 'pri_01jsh6fef0zevaa49xr99c25q3';
const YEARLY_PRICE_ID = 'pri_01jsh6wb4brwkpbmqhp02kh9xb';

type SubscriptionStatus = 'free' | 'active' | 'canceled' | 'expired';

interface SubscriptionContextType {
  isSubscribed: boolean;
  subscriptionStatus: SubscriptionStatus;
  freeConversionsUsed: number;
  freeConversionsLimit: number;
  hasRemainingFreeConversions: boolean;
  incrementFreeConversions: () => void;
  checkoutMonthly: () => void;
  checkoutYearly: () => void;
  resetFreeConversions: () => void;
  refreshSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [freeConversionsUsed, setFreeConversionsUsed] = useState<number>(0);
  // Start from the last known status so the UI doesn't flash "Go Premium" on reload;
  // the real status is re-checked from Supabase right after.
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(() => {
    try {
      const cached = localStorage.getItem('subscriptionStatus');
      return cached === 'active' ? 'active' : 'free';
    } catch {
      return 'free';
    }
  });
  const freeConversionsLimit = 3;
  // Bumping this re-runs the status fetch (e.g. polling on the thank-you page)
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshSubscription = useCallback(() => setRefreshKey(k => k + 1), []);
  const [paddleInstance, setPaddleInstance] = useState<any>(null);
  const { user } = useAuth();
  // Keep a ref to user so the Paddle callback can access the latest value
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // Initialize Paddle.js once on mount
  useEffect(() => {
    const initializePaddleJS = async () => {
      try {
        const paddle = await initializePaddle({
          token: 'live_084ba83b93353d2c956c3678ffa',
          pwCustomer: {},
          // ─── Checkout event callback ───────────────────────────────────────
          // Fires when a transaction completes in the Paddle overlay.
          // We use this to immediately mark the user as premium in Supabase
          // without waiting for the webhook (which can fail silently).
          eventCallback: async (event: any) => {
            console.log('[Paddle Event]', event?.name, event);
            if (
              event?.name === 'checkout.completed' ||
              event?.name === 'transaction.completed'
            ) {
              const currentUser = userRef.current;
              if (currentUser) {
                try {
                  // Immediately upgrade in Supabase
                  const userId = currentUser.id || currentUser.uid;
                  await supabase
                    .from('user_profiles')
                    .update({
                      subscription_status: 'premium',
                      updated_at: new Date().toISOString()
                    })
                    .eq('user_id', userId);
                  
                  localStorage.setItem('subscriptionStatus', 'active');
                  setSubscriptionStatus('active'); // Instantly update UI locally
                  console.log('[Paddle] Supabase upgraded to premium for:', currentUser.email);
                } catch (e) {
                  console.warn('[Paddle] Failed to update Supabase after checkout:', e);
                  // Fallback: at least set localStorage so the user isn't blocked
                  localStorage.setItem('subscriptionStatus', 'active');
                  setSubscriptionStatus('active');
                }
              } else {
                // Not logged in (guest checkout), set local status
                localStorage.setItem('subscriptionStatus', 'active');
                setSubscriptionStatus('active');
              }
            }
          },
        });
        setPaddleInstance(paddle);
        console.log('Paddle.js initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Paddle.js:', error);
      }
    };

    initializePaddleJS();

    // Initialize free conversions from localStorage
    const storedConversions = localStorage.getItem('freeConversionsUsed');
    if (storedConversions) {
      setFreeConversionsUsed(parseInt(storedConversions, 10));
    }
  }, []);

  // ─── PRIMARY: Real-time Supabase subscription listener ───────────────────
  // When the webhook (or checkout callback above) writes subscription_status: 'premium',
  // this channel listener fires and sets isSubscribed = true immediately.
  useEffect(() => {
    let channel: any = null;

    if (!user) {
      // Guest flow: check guest session from Supabase
      const guestSession = getGuestSession();
      if (!guestSession) {
        setSubscriptionStatus('free');
        localStorage.setItem('subscriptionStatus', 'free');
        return;
      }

      // Sync initial guest session status from DB
      const fetchGuestSubscriptionStatus = async () => {
        try {
          const { data, error } = await supabase
            .from('guest_sessions')
            .select('subscription_status')
            .eq('id', guestSession.id)
            .maybeSingle();

          if (data) {
            const status = data.subscription_status === 'active' ? 'active' : 'free';
            setSubscriptionStatus(status as SubscriptionStatus);
            localStorage.setItem('subscriptionStatus', status);
            console.log(`[SubscriptionContext] Initial guest fetch status: ${status}`);
          } else {
            // Fall back to local storage if not found in DB
            const localStatus = localStorage.getItem('subscriptionStatus');
            if (localStatus && ['free', 'active', 'canceled', 'expired'].includes(localStatus)) {
              setSubscriptionStatus(localStatus as SubscriptionStatus);
            } else {
              setSubscriptionStatus('free');
            }
          }
        } catch (err) {
          console.error('Error fetching guest subscription status:', err);
        }
      };

      fetchGuestSubscriptionStatus();

      // Listen to real-time updates on guest_sessions table for this guest session
      channel = supabase
        .channel(`guest-session-status-${guestSession.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'guest_sessions',
            filter: `id=eq.${guestSession.id}`
          },
          (payload: any) => {
            const session = payload.new;
            if (session) {
              const status = session.subscription_status === 'active' ? 'active' : 'free';
              setSubscriptionStatus(status as SubscriptionStatus);
              localStorage.setItem('subscriptionStatus', status);
              console.log(`[SubscriptionContext] Guest real-time status update: ${status}`);
            }
          }
        )
        .subscribe();

      return () => {
        if (channel) supabase.removeChannel(channel);
      };
    }

    // Logged in user flow
    const userId = user.id || user.uid;
    const userEmail: string = user.email || '';

    // Premium if the profile says so, OR if Paddle has an active subscription for this email.
    // The second check covers subscribers from before the Firebase -> Supabase migration
    // (their new Supabase profile starts as 'none') and missed webhooks.
    const resolveStatus = async (profileStatus?: string): Promise<SubscriptionStatus> => {
      if (profileStatus === 'premium') return 'active';
      if (userEmail) {
        try {
          if (await hasActiveSubscriptionByEmail(userEmail)) return 'active';
        } catch (e) {
          console.warn('[SubscriptionContext] Email subscription check failed:', e);
        }
      }
      return 'free';
    };

    const fetchSubscriptionStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('subscription_status')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) console.warn('[SubscriptionContext] Profile fetch error:', error);
        const status = await resolveStatus(data?.subscription_status);
        setSubscriptionStatus(status);
        localStorage.setItem('subscriptionStatus', status);
        console.log(`[SubscriptionContext] Initial fetch status: ${status}`);
      } catch (err) {
        console.error('Error fetching subscription status from Supabase:', err);
      }
    };

    fetchSubscriptionStatus();

    // Listen to real-time updates of the user_profiles table for this user
    channel = supabase
      .channel(`subscription-status-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `user_id=eq.${userId}`
        },
        async (payload: any) => {
          const profile = payload.new;
          if (profile) {
            const status = await resolveStatus(profile.subscription_status);
            setSubscriptionStatus(status);
            localStorage.setItem('subscriptionStatus', status);
            console.log(`[SubscriptionContext] Real-time status update: ${status}`);
          }
        }
      )
      .subscribe();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [user, refreshKey]);

  const incrementFreeConversions = () => {
    const newCount = freeConversionsUsed + 1;
    setFreeConversionsUsed(newCount);
    localStorage.setItem('freeConversionsUsed', newCount.toString());
  };

  const resetFreeConversions = () => {
    setFreeConversionsUsed(0);
    localStorage.setItem('freeConversionsUsed', '0');
  };

  const checkoutMonthly = async () => {
    if (paddleInstance) {
      try {
        let guestSession = getGuestSession();
        if (!guestSession) {
          guestSession = await createGuestSession(user?.email || undefined);
        }

        paddleInstance.Checkout.open({
          items: [{ priceId: MONTHLY_PRICE_ID, quantity: 1 }],
          customer: user?.email ? { email: user.email } : undefined,
          customData: { 
            guestSessionId: guestSession.id,
            userId: user?.id || user?.uid || undefined,
            customerEmail: user?.email || undefined
          },
          settings: { ...PADDLE_CHECKOUT_SETTINGS, allowLogout: false }
        });
      } catch (error) {
        console.error('Failed to open Paddle checkout:', error);
      }
    } else {
      console.error('Paddle instance not initialized');
    }
  };

  const checkoutYearly = async () => {
    if (paddleInstance) {
      try {
        let guestSession = getGuestSession();
        if (!guestSession) {
          guestSession = await createGuestSession(user?.email || undefined);
        }

        paddleInstance.Checkout.open({
          items: [{ priceId: YEARLY_PRICE_ID, quantity: 1 }],
          customer: user?.email ? { email: user.email } : undefined,
          customData: { 
            guestSessionId: guestSession.id,
            userId: user?.id || user?.uid || undefined,
            customerEmail: user?.email || undefined
          },
          settings: { ...PADDLE_CHECKOUT_SETTINGS, allowLogout: false }
        });
      } catch (error) {
        console.error('Failed to open Paddle checkout:', error);
      }
    } else {
      console.error('Paddle instance not initialized');
    }
  };

  const hasRemainingFreeConversions = freeConversionsUsed < freeConversionsLimit;
  const isSubscribed = subscriptionStatus === 'active';

  return (
    <SubscriptionContext.Provider
      value={{
        isSubscribed,
        subscriptionStatus,
        freeConversionsUsed,
        freeConversionsLimit,
        hasRemainingFreeConversions,
        incrementFreeConversions,
        checkoutMonthly,
        checkoutYearly,
        resetFreeConversions,
        refreshSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};